import { localizationService } from './localization';

export interface ValidationRule<T = any> {
  validate: (value: T) => boolean;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface BusinessRule<T = any> {
  name: string;
  validate: (data: T) => Promise<boolean> | boolean;
  message: string;
}

/**
 * Service for data validation and business rule enforcement
 * Requirements: 4.4, 6.5
 */
export class ValidationService {
  /**
   * Validate program name
   */
  validateProgramName(name: string): ValidationResult {
    const errors: string[] = [];

    // Required
    if (!name || !name.trim()) {
      errors.push('Название программы обязательно');
    }

    // Length
    if (name && name.length > 100) {
      errors.push('Название программы не должно превышать 100 символов');
    }

    // Cyrillic validation
    if (name && name.trim() && !localizationService.validateCyrillicText(name)) {
      errors.push('Используйте только русские буквы, цифры и знаки препинания');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate workout name
   */
  validateWorkoutName(name: string): ValidationResult {
    const errors: string[] = [];

    // Required
    if (!name || !name.trim()) {
      errors.push('Название тренировки обязательно');
    }

    // Length
    if (name && name.length > 50) {
      errors.push('Название тренировки не должно превышать 50 символов');
    }

    // Cyrillic validation
    if (name && name.trim() && !localizationService.validateCyrillicText(name)) {
      errors.push('Используйте только русские буквы, цифры и знаки препинания');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate set data (weight, reps)
   */
  validateSetData(weight: number, reps: number): ValidationResult {
    const errors: string[] = [];

    // Weight validation
    if (weight < 0) {
      errors.push('Вес не может быть отрицательным');
    }
    if (weight > 1000) {
      errors.push('Вес не может превышать 1000 кг');
    }
    if (!Number.isFinite(weight)) {
      errors.push('Вес должен быть числом');
    }

    // Reps validation
    if (reps < 0) {
      errors.push('Количество повторений не может быть отрицательным');
    }
    if (reps > 1000) {
      errors.push('Количество повторений не может превышать 1000');
    }
    if (!Number.isInteger(reps)) {
      errors.push('Количество повторений должно быть целым числом');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate target sets and reps for program exercises
   */
  validateTargetData(sets?: number, repsMin?: number, repsMax?: number): ValidationResult {
    const errors: string[] = [];

    // Sets validation
    if (sets !== undefined) {
      if (sets < 1) {
        errors.push('Количество подходов должно быть больше 0');
      }
      if (sets > 50) {
        errors.push('Количество подходов не может превышать 50');
      }
      if (!Number.isInteger(sets)) {
        errors.push('Количество подходов должно быть целым числом');
      }
    }

    // Reps range validation
    if (repsMin !== undefined) {
      if (repsMin < 1) {
        errors.push('Минимальное количество повторений должно быть больше 0');
      }
      if (repsMin > 1000) {
        errors.push('Минимальное количество повторений не может превышать 1000');
      }
      if (!Number.isInteger(repsMin)) {
        errors.push('Минимальное количество повторений должно быть целым числом');
      }
    }

    if (repsMax !== undefined) {
      if (repsMax < 1) {
        errors.push('Максимальное количество повторений должно быть больше 0');
      }
      if (repsMax > 1000) {
        errors.push('Максимальное количество повторений не может превышать 1000');
      }
      if (!Number.isInteger(repsMax)) {
        errors.push('Максимальное количество повторений должно быть целым числом');
      }
    }

    // Range consistency
    if (repsMin !== undefined && repsMax !== undefined && repsMin > repsMax) {
      errors.push('Минимальное количество повторений не может быть больше максимального');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate comment text
   */
  validateComment(comment: string): ValidationResult {
    const errors: string[] = [];

    // Length
    if (comment && comment.length > 500) {
      errors.push('Комментарий не должен превышать 500 символов');
    }

    // Cyrillic validation (if not empty)
    if (comment && comment.trim() && !localizationService.validateCyrillicText(comment)) {
      errors.push('Используйте только русские буквы, цифры и знаки препинания');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate workout duration
   */
  validateWorkoutDuration(minutes: number): ValidationResult {
    const errors: string[] = [];

    if (minutes < 1) {
      errors.push('Длительность тренировки должна быть больше 0 минут');
    }
    if (minutes > 600) { // 10 hours
      errors.push('Длительность тренировки не может превышать 10 часов');
    }
    if (!Number.isFinite(minutes)) {
      errors.push('Длительность должна быть числом');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate assistance weight
   */
  validateAssistanceWeight(assistance: string): ValidationResult {
    const errors: string[] = [];

    if (assistance && assistance.trim()) {
      // Should be a number followed by optional unit
      const assistancePattern = /^-?\d+(\.\d+)?\s*(кг|kg)?$/i;
      if (!assistancePattern.test(assistance.trim())) {
        errors.push('Помощь должна быть в формате: "-10кг" или "5"');
      }

      // Extract number and validate range
      const match = assistance.match(/-?\d+(\.\d+)?/);
      if (match) {
        const value = parseFloat(match[0]);
        if (Math.abs(value) > 500) {
          errors.push('Помощь не может превышать 500 кг');
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Business rule: Only one active program per user
   */
  async validateSingleActiveProgram(
    programs: Array<{ id: string; is_active: boolean }>,
    newActiveProgramId?: string
  ): Promise<ValidationResult> {
    const errors: string[] = [];

    const activePrograms = programs.filter(p => 
      p.is_active && (!newActiveProgramId || p.id !== newActiveProgramId)
    );

    if (newActiveProgramId && activePrograms.length > 0) {
      errors.push('У вас уже есть активная программа. Деактивируйте её перед активацией новой.');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Business rule: Program must have at least one workout
   */
  validateProgramHasWorkouts(workoutCount: number): ValidationResult {
    const errors: string[] = [];

    if (workoutCount === 0) {
      errors.push('Программа должна содержать хотя бы одну тренировку');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Business rule: Workout must have at least one exercise
   */
  validateWorkoutHasExercises(exerciseCount: number): ValidationResult {
    const errors: string[] = [];

    if (exerciseCount === 0) {
      errors.push('Тренировка должна содержать хотя бы одно упражнение');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Business rule: Exercise cannot be duplicated in the same workout
   */
  validateNoDuplicateExercises(exerciseIds: string[]): ValidationResult {
    const errors: string[] = [];

    const uniqueIds = new Set(exerciseIds);
    if (uniqueIds.size !== exerciseIds.length) {
      errors.push('Упражнения не должны повторяться в одной тренировке');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Business rule: Workout session must have at least one completed set
   */
  validateWorkoutHasCompletedSets(
    exercises: Array<{ sets: Array<{ weight: number; reps: number }> }>
  ): ValidationResult {
    const errors: string[] = [];

    const totalSets = exercises.reduce((total, exercise) => total + exercise.sets.length, 0);
    
    if (totalSets === 0) {
      errors.push('Тренировка должна содержать хотя бы один выполненный подход');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Comprehensive validation for creating a program
   */
  async validateProgramCreation(data: {
    name: string;
    workouts: Array<{
      name: string;
      exercises: Array<{
        exerciseId: string;
        targetSets?: number;
        targetRepsMin?: number;
        targetRepsMax?: number;
      }>;
    }>;
  }): Promise<ValidationResult> {
    const errors: string[] = [];

    // Validate program name
    const nameValidation = this.validateProgramName(data.name);
    errors.push(...nameValidation.errors);

    // Validate program has workouts
    const workoutCountValidation = this.validateProgramHasWorkouts(data.workouts.length);
    errors.push(...workoutCountValidation.errors);

    // Validate each workout
    for (let i = 0; i < data.workouts.length; i++) {
      const workout = data.workouts[i];
      
      // Validate workout name
      const workoutNameValidation = this.validateWorkoutName(workout.name);
      errors.push(...workoutNameValidation.errors.map(err => `Тренировка ${i + 1}: ${err}`));

      // Validate workout has exercises
      const exerciseCountValidation = this.validateWorkoutHasExercises(workout.exercises.length);
      errors.push(...exerciseCountValidation.errors.map(err => `Тренировка "${workout.name}": ${err}`));

      // Validate no duplicate exercises
      const exerciseIds = workout.exercises.map(e => e.exerciseId);
      const duplicateValidation = this.validateNoDuplicateExercises(exerciseIds);
      errors.push(...duplicateValidation.errors.map(err => `Тренировка "${workout.name}": ${err}`));

      // Validate target data for each exercise
      for (let j = 0; j < workout.exercises.length; j++) {
        const exercise = workout.exercises[j];
        const targetValidation = this.validateTargetData(
          exercise.targetSets,
          exercise.targetRepsMin,
          exercise.targetRepsMax
        );
        errors.push(...targetValidation.errors.map(err => 
          `Тренировка "${workout.name}", упражнение ${j + 1}: ${err}`
        ));
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Comprehensive validation for completing a workout
   */
  validateWorkoutCompletion(data: {
    exercises: Array<{
      exercise_id: string;
      sets: Array<{ weight: number; reps: number; assistance?: string; notes?: string }>;
      notes?: string;
    }>;
    notes?: string;
    duration_minutes: number;
  }): ValidationResult {
    const errors: string[] = [];

    // Validate duration
    const durationValidation = this.validateWorkoutDuration(data.duration_minutes);
    errors.push(...durationValidation.errors);

    // Validate workout has completed sets
    const setsValidation = this.validateWorkoutHasCompletedSets(data.exercises);
    errors.push(...setsValidation.errors);

    // Validate workout notes
    if (data.notes) {
      const notesValidation = this.validateComment(data.notes);
      errors.push(...notesValidation.errors.map(err => `Комментарий к тренировке: ${err}`));
    }

    // Validate each exercise
    for (let i = 0; i < data.exercises.length; i++) {
      const exercise = data.exercises[i];

      // Validate exercise notes
      if (exercise.notes) {
        const exerciseNotesValidation = this.validateComment(exercise.notes);
        errors.push(...exerciseNotesValidation.errors.map(err => 
          `Упражнение ${i + 1}, комментарий: ${err}`
        ));
      }

      // Validate each set
      for (let j = 0; j < exercise.sets.length; j++) {
        const set = exercise.sets[j];

        // Validate set data
        const setValidation = this.validateSetData(set.weight, set.reps);
        errors.push(...setValidation.errors.map(err => 
          `Упражнение ${i + 1}, подход ${j + 1}: ${err}`
        ));

        // Validate assistance
        if (set.assistance) {
          const assistanceValidation = this.validateAssistanceWeight(set.assistance);
          errors.push(...assistanceValidation.errors.map(err => 
            `Упражнение ${i + 1}, подход ${j + 1}, помощь: ${err}`
          ));
        }

        // Validate set notes
        if (set.notes) {
          const setNotesValidation = this.validateComment(set.notes);
          errors.push(...setNotesValidation.errors.map(err => 
            `Упражнение ${i + 1}, подход ${j + 1}, комментарий: ${err}`
          ));
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
export const validationService = new ValidationService();